import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fmtDate } from "../lib/api";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function PublicReportPage() {
    const { token } = useParams();
    const [r, setR] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        axios.get(`${BACKEND}/api/r/${token}`)
            .then(({ data }) => setR(data))
            .catch((e) => setErr(e.response?.data?.detail || "Report not available"));
    }, [token]);

    if (err) return (
        <div className="min-h-screen bg-[#F4F6F9] flex items-center justify-center p-6">
            <div className="max-w-md text-center">
                <div className="font-display font-bold text-2xl text-[#1E3355] mb-2">Flexworx Ops</div>
                <div className="text-sm text-gray-500 mb-4">A Flexworx Company</div>
                <div className="rounded-xl bg-white p-8 border border-gray-100"><div className="text-[#E02B2B] font-semibold text-lg mb-1">Report unavailable</div><div className="text-sm text-gray-600">{err}</div></div>
            </div>
        </div>
    );
    if (!r) return <div className="min-h-screen bg-[#F4F6F9] p-6"><div className="max-w-4xl mx-auto h-40 rounded-xl bg-white border border-gray-100 animate-pulse" /></div>;

    const d = r.data;
    const enabled = r.sections_enabled || {};
    const selectedPhotos = (r.photo_ids || []).map((id) => d.photos.find((p) => p.id === id)).filter(Boolean);

    return (
        <div className="min-h-screen bg-[#F4F6F9] print:bg-white">
            <style>{`@media print {
                .no-print { display: none; }
                body { background: white; }
                .print-card { border: 1px solid #E5E7EB !important; box-shadow: none !important; break-inside: avoid; }
            }`}</style>
            <header className="bg-[#1E3355] text-white py-5 sm:py-6 print:py-4" data-testid="public-report-header">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="font-display font-bold text-xl sm:text-2xl">Flexworx Ops</div>
                        <div className="text-[11px] uppercase tracking-widest text-white/70">A Flexworx Company</div>
                    </div>
                    <div className="text-right text-xs sm:text-sm">
                        <div className="font-semibold">{d.site.name}</div>
                        <div className="text-white/70">Monthly report · {r.month} · v{r.version}</div>
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-4" data-testid="public-report-body">
                {r.intro_note && enabled.checklist !== false && (
                    <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-5 print-card">
                        <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-2">Introduction</div>
                        <div className="text-sm text-gray-700 whitespace-pre-line">{r.intro_note}</div>
                    </div>
                )}

                {enabled.checklist && (
                    <Section title="Checklist completion">
                        <table className="w-full text-xs"><thead className="text-[10px] text-gray-500 text-left uppercase tracking-wider"><tr><th className="pr-2 pb-1">Date</th><th className="pr-2">Theme</th><th className="pr-2">Tasks</th><th className="pr-2">Critical</th><th className="pr-2">Esc</th><th className="pr-2">Sign-off</th><th>Manager</th></tr></thead>
                        <tbody className="divide-y divide-gray-100">{d.checklist_rows.map((row) => (
                            <tr key={row.id}><td className="py-1 pr-2 whitespace-nowrap">{fmtDate(row.date)}</td><td className="pr-2 truncate max-w-[150px]">{row.theme}</td><td className="pr-2">{row.done}/{row.total}</td><td className="pr-2">{row.crit_done}/{row.crit_total}</td><td className="pr-2">{row.escalations}</td><td className="pr-2">{row.signoff_time ? row.signoff_time.slice(11, 16) : "—"}</td><td>{row.manager_name}</td></tr>
                        ))}</tbody></table>
                    </Section>
                )}

                {enabled.service_requests && d.sr_rows.length > 0 && (
                    <Section title="Service requests">
                        <ul className="divide-y divide-gray-100 text-sm">{d.sr_rows.map((r2) => (
                            <li key={r2.id} className="py-2"><div className="flex justify-between gap-2"><div><div className="font-semibold text-[#1E3355]">{r2.category}</div><div className="text-gray-600 text-xs">{r2.description}</div></div><div className="text-xs text-gray-500 whitespace-nowrap text-right">{r2.status} · ack {r2.response_minutes ? Math.round(r2.response_minutes / 60) + "h" : "—"} · close {r2.resolution_minutes ? Math.round(r2.resolution_minutes / 60) + "h" : "—"}</div></div></li>
                        ))}</ul>
                    </Section>
                )}

                {enabled.attendance && d.attendance.length > 0 && (
                    <Section title="Attendance">
                        <table className="w-full text-xs"><thead className="text-[10px] text-gray-500 text-left uppercase tracking-wider"><tr><th>Role</th><th>Present</th><th>Absent</th><th>Cover arranged</th><th>Rule violations</th></tr></thead>
                        <tbody className="divide-y divide-gray-100">{d.attendance.map((row) => (
                            <tr key={row.role}><td className="py-1 font-semibold">{row.role}</td><td>{row.present}</td><td>{row.absent}</td><td>{row.cover_arranged}</td><td className={row.violations > 0 ? "text-[#E02B2B] font-semibold" : ""}>{row.violations}</td></tr>
                        ))}</tbody></table>
                    </Section>
                )}

                {enabled.consumables && (d.deliveries.length > 0 || d.stockouts.length > 0) && (
                    <Section title="Consumables & deliveries">
                        {d.deliveries.length > 0 && <><div className="text-[11px] font-semibold text-gray-500 mb-1">Delivered</div><ul className="text-xs mb-3 space-y-0.5">{d.deliveries.map((x, i) => <li key={i}>· {x.item_name} — qty {x.qty_received} · {fmtDate(x.delivered_at)}</li>)}</ul></>}
                        {d.stockouts.length > 0 && <><div className="text-[11px] font-semibold text-gray-500 mb-1">Stockout events</div><ul className="text-xs space-y-0.5">{d.stockouts.map((x) => <li key={x.id} className={x.resolved_at ? "" : "text-[#E02B2B]"}>· {x.item_name} — {fmtDate(x.created_at)} {x.resolved_at ? `(resolved)` : "(open)"}</li>)}</ul></>}
                    </Section>
                )}

                {enabled.usage_logs && (d.usage.beyond_hours_total > 0 || d.usage.dg_total > 0) && (
                    <Section title="Beyond-hours & DG usage">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="rounded-md bg-[#F4F6F9] p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">Beyond-hours total</div><div className="font-display font-bold text-[#1E3355] text-lg">{d.usage.beyond_hours_total}h</div></div>
                            <div className="rounded-md bg-[#F4F6F9] p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">DG runtime total</div><div className="font-display font-bold text-[#1E3355] text-lg">{d.usage.dg_total}h</div></div>
                        </div>
                    </Section>
                )}

                {enabled.compliance && (
                    <Section title="Compliance">
                        <div className="text-[11px] font-semibold text-gray-500 mb-1">Status</div>
                        <ul className="text-xs mb-3 space-y-0.5">{d.compliance_status.map((c) => (
                            <li key={c.id}>· {c.name} — <span className={c.status === "OVERDUE" ? "text-[#E02B2B] font-semibold" : c.status === "DUE_SOON" ? "text-amber-700 font-semibold" : "text-emerald-700 font-semibold"}>{c.status}</span> · next {fmtDate(c.next_due_date)}</li>
                        ))}</ul>
                        {d.compliance_visits.length > 0 && <><div className="text-[11px] font-semibold text-gray-500 mb-1">Visits this month</div><ul className="text-xs space-y-0.5">{d.compliance_visits.map((v) => <li key={v.id}>· {fmtDate(v.visit_date)} · {v.vendor || "—"}</li>)}</ul></>}
                    </Section>
                )}

                {enabled.special_jobs && d.special_jobs && (
                    <Section title="Special jobs completed">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3" data-testid="public-special-jobs-counts">
                            <PubStat label="Total jobs" value={d.special_jobs.total_jobs} />
                            <PubStat label="Done" value={d.special_jobs.done} />
                            <PubStat label="Verified" value={d.special_jobs.verified} />
                            <PubStat label="Carried over" value={d.special_jobs.carried_over} />
                        </div>
                        {d.special_jobs.notable?.length > 0 && (<>
                            <div className="text-[11px] font-semibold text-gray-500 mb-1">Notable jobs</div>
                            <ul className="text-xs space-y-0.5" data-testid="public-special-jobs-notable">
                                {d.special_jobs.notable.map((j, i) => (
                                    <li key={i}>· {fmtDate(j.date)} · {j.description} · {j.assignee}{j.verified ? " · verified" : ""}</li>
                                ))}
                            </ul>
                        </>)}
                    </Section>
                )}

                {enabled.photos && selectedPhotos.length > 0 && (
                    <Section title="Photos">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {selectedPhotos.map((p) => (
                                <div key={p.id} className="rounded-md overflow-hidden border border-gray-200">
                                    <img src={`${BACKEND}/api/public/reports/${token}/photos/${p.id}/blob`} alt="" className="w-full h-40 sm:h-48 object-cover" />
                                    <div className="px-2 py-1 text-[10px] text-gray-500">{fmtDate(p.created_at)} · {p.uploader_name}</div>
                                </div>
                            ))}
                        </div>
                    </Section>
                )}

                <footer className="text-center text-[11px] text-gray-500 pt-8 pb-4">
                    Published {r.published_at ? fmtDate(r.published_at) : ""} · Flexworx Ops · A Flexworx Company
                </footer>
                <div className="no-print flex justify-center gap-2 pb-8">
                    <button onClick={() => window.print()} className="rounded-md bg-[#1E3355] text-white px-4 py-2 text-sm font-semibold hover:bg-[#15243C]" data-testid="public-print">Print / Save as PDF</button>
                </div>
            </main>
        </div>
    );
}

function Section({ title, children }) {
    return (<div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4 sm:p-5 print-card">
        <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-3">{title}</div>
        {children}
    </div>);
}

function PubStat({ label, value }) {
    return (<div className="rounded-md border border-gray-100 bg-[#F4F6F9] px-3 py-2">
        <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{label}</div>
        <div className="font-display text-xl font-bold text-[#1E3355]">{value ?? 0}</div>
    </div>);
}

